/**
 * web/src/components/gates/SkipConfirm.tsx
 * Confirmation dialog before skipping an email-gate. Used when the reviewer
 * already sent the email outside the system. The workflow advances without
 * sending; the receipt step cascades to skipped.
 */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../shared/Button';
import { Icon } from '../../lib/icons';

/** Public props for SkipConfirm. */
export interface SkipConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the reviewer clicks Skip step. */
  onConfirm: () => void;
  /** When true, disables the confirm button (e.g., while the mutation is pending). */
  isSubmitting?: boolean;
  /** Copy overrides (default: the email-gate "already sent outside" wording). */
  body?: string;
  note?: string;
}

/**
 * Skip confirmation modal. The action is irreversible — the workflow advances
 * to the next step. Reviewer should only choose Skip when they have already
 * sent the email through another channel.
 *
 * @sideEffects none — caller owns the open state and the confirm handler
 * @idempotent yes
 */
export function SkipConfirm({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
  body,
  note,
}: SkipConfirmProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Skip without sending?</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="mb-3 text-text">
            {body ??
              "Use this when you've already sent the email outside the system. The workflow will " +
                'advance and Pixel will mark the email as sent in the Sponsorship Tracker, but no email ' +
                'will be sent from here.'}
          </p>
          <p className="text-text-muted text-[12px]">
            {note ??
              'The receipt-tracking step will also be skipped — manage replies in the channel you ' +
                'sent from.'}
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="default" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={isSubmitting}>
            <Icon name="check" size={12} />
            Skip step
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
